import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

// Shared
import { useAuth } from '@/common/appContext';
import { useData } from '@/common/dataContext';

// Relative imports
import {
  isCompanyValuesSetupAllowlistedPath,
  resolveCompanyValuesSetupState,
} from './core/companyValuesSetupState';
import {
  buildGettingStartedPath,
  isGettingStartedAllowlistedPath,
  resolveGettingStartedState,
} from './core/gettingStartedState';

/**
 * Post-login founder gates. A pending checklist wins, and company values is its first step.
 * Without a checklist, pending values still open `/setup/company-values`.
 */
export function CompanyValuesSetupGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { employeeID } = useAuth();
  const { profile } = useData();
  const valuesSetup = resolveCompanyValuesSetupState(profile, employeeID);
  const gettingStarted = resolveGettingStartedState(profile, employeeID);

  if (
    gettingStarted.shouldGate &&
    gettingStarted.campaignSlug &&
    !isGettingStartedAllowlistedPath(location.pathname)
  ) {
    return <Navigate to={buildGettingStartedPath(gettingStarted.campaignSlug)} replace />;
  }

  if (
    valuesSetup.shouldGate &&
    !gettingStarted.shouldGate &&
    !isCompanyValuesSetupAllowlistedPath(location.pathname)
  ) {
    return <Navigate to="/setup/company-values" replace />;
  }

  return children;
}
